/**
 * useStackRealtimeSse — SSE-based data path for the StackDetailsPage.
 *
 * Opens 2 SSE connections:
 *   1. sse/stack/:alias — deployment events, status changes, gitops, pipeline runs, logs
 *   2. sse/pods/:alias  — pod snapshots + K8s events (Redis Pub/Sub)
 *
 * EventSource auto-reconnects natively, so there is no polling fallback.
 * On disconnect, a one-shot REST refresh primes the data; SSE resumes the
 * stream when the connection is restored.
 */

import { useCallback, useEffect, useState } from "react";

import { useSse, type SseMessage } from "@platform/hooks/useSse";

import { normalizeEvent } from "./normalizeEvent";
import type { DeploymentEvent } from "./useDeploymentEvents";
import type {
  PipelineStep,
  WsDeploymentEvent,
  WsLogBatchEvent,
} from "./useDeploymentStream";
import type { GitOpsStatus } from "./useGitOpsStatus";
import type { K8sEventSnapshot } from "./useK8sEvents";
import { invalidatePipelineRunSteps } from "./usePipelineRunSteps";
import type { PodMonitorSnapshot } from "./usePodMonitor";
import type { StackRealtimePollingState } from "./useStackRealtimePolling";

export interface StackRealtimeSocketState {
  deploymentConnected: boolean;
  podsConnected: boolean;
}

export function useStackRealtimeSse(
  alias: string,
  appEnv: string,
  polling: StackRealtimePollingState,
): StackRealtimeSocketState {
  const {
    setEvents,
    setPipelineState,
    setLogBatches,
    setDeploymentError,
    setStatus,
    setDeploymentUrl,
    setPodSnapshot,
    setPodError,
    setK8sEvents,
    setK8sEventsError,
    setGitopsStatus,
    setPipelineRunRefreshSignal,
    fetchDeploymentEvents,
    fetchPipelineState,
    fetchMetrics,
    fetchPods,
    fetchK8sEvents,
  } = polling;

  const [deploymentConnected, setDeploymentConnected] = useState(false);
  const [podsConnected, setPodsConnected] = useState(false);

  // ── SSE 1: deployment events (sse/stack/:alias) ──────────────────────────

  const handleDeploymentMessage = useCallback(
    (msg: SseMessage) => {
      if (msg.type === "connected") {
        setDeploymentConnected(true);
        setDeploymentError(null);
        return;
      }

      if (msg.type === "deployment") {
        const data = msg.data as WsDeploymentEvent;
        const raw: DeploymentEvent = {
          id: `${data.jobId}-${data.eventType}-${data.timestamp}`,
          sagaId: data.jobId,
          eventType: data.eventType,
          systemId: data.systemId,
          systemAlias: data.systemAlias,
          organizationId: data.organizationId,
          step: data.step,
          status:
            data.status ??
            (data.severity === "error" ? "failed" : "in_progress"),
          severity: data.severity,
          message: data.message,
          metadata: data.metadata,
          retryCount: 0,
          createdAt: data.timestamp,
          updatedAt: data.timestamp,
        };
        const event = normalizeEvent(raw);
        setEvents((prev) => [event, ...prev]);
        const serviceName =
          (data.metadata?.serviceName as string | undefined) ?? "";
        setPipelineState((prev) => {
          const existing = prev.find(
            (step) =>
              step.sagaId === data.jobId &&
              step.step === data.step &&
              step.serviceName === serviceName,
          );
          const nextStep: PipelineStep = {
            id: existing?.id ?? -1,
            systemId: data.systemId,
            systemAlias: data.systemAlias,
            step: data.step,
            serviceName,
            status: data.status ?? "in_progress",
            severity: data.severity,
            message: data.message,
            eventType: data.eventType,
            sagaId: data.jobId,
            startedAt: existing?.startedAt ?? data.timestamp,
            completedAt:
              data.status === "completed" || data.status === "failed"
                ? data.timestamp
                : (existing?.completedAt ?? null),
            updatedAt: data.timestamp,
          };
          return [
            nextStep,
            ...prev.filter(
              (step) =>
                !(
                  step.sagaId === data.jobId &&
                  step.step === data.step &&
                  step.serviceName === serviceName
                ),
            ),
          ];
        });
        invalidatePipelineRunSteps(data.jobId);
        setDeploymentError(null);

        // Handle deploy completed/failed (carry implicit status)
        const eventType = data.eventType;
        if (
          eventType === "deploy_completed" ||
          eventType === "DEPLOY_COMPLETED"
        ) {
          setStatus("running");
          if (data.metadata?.ingressUrl) {
            setDeploymentUrl(data.metadata.ingressUrl as string);
          }
        } else if (
          eventType === "deploy_failed" ||
          eventType === "DEPLOY_FAILED"
        ) {
          setStatus("failed");
        }

        return;
      }

      if (msg.type === "deployment.log") {
        const data = msg.data as WsLogBatchEvent;
        setLogBatches((prev) => {
          const next = new Map(prev);
          const sagaMap = new Map(
            next.get(data.sagaId) ?? new Map<string, Map<string, string[]>>(),
          );
          const serviceMap = new Map(
            sagaMap.get(data.service) ?? new Map<string, string[]>(),
          );
          const existingLines = serviceMap.get(data.phase) ?? [];
          const merged =
            data.offset <= existingLines.length
              ? [...existingLines.slice(0, data.offset), ...data.lines]
              : [...existingLines, ...data.lines];
          serviceMap.set(data.phase, merged);
          sagaMap.set(data.service, serviceMap);
          next.set(data.sagaId, sagaMap);
          return next;
        });
        return;
      }

      if (msg.type === "status_changed") {
        const data = msg.data as { systemAlias: string; newStatus: string };
        if (data.systemAlias === alias) {
          setStatus(data.newStatus);
        }
        return;
      }

      if (msg.type === "gitops_status") {
        const payload = msg.data as {
          newStatus: string;
          healthStatus: string;
          revision: string;
        };
        setGitopsStatus((prev) =>
          prev
            ? {
                ...prev,
                syncStatus: payload.newStatus as GitOpsStatus["syncStatus"],
                healthStatus: payload.healthStatus,
                revision: payload.revision,
                lastSyncAt: new Date().toISOString(),
              }
            : prev,
        );
        return;
      }

      if (msg.type === "pipeline_run") {
        setPipelineRunRefreshSignal((n) => n + 1);
        return;
      }
    },
    [
      alias,
      setEvents,
      setPipelineState,
      setLogBatches,
      setDeploymentError,
      setStatus,
      setDeploymentUrl,
      setGitopsStatus,
      setPipelineRunRefreshSignal,
    ],
  );

  const deploymentSse = useSse(
    alias ? `sse/stack/${alias}` : null,
    handleDeploymentMessage,
  );

  // Sync deployment SSE connection state. On disconnect, a one-shot REST
  // refresh primes the data — EventSource reconnects natively, so no
  // polling interval is needed.
  useEffect(() => {
    setDeploymentConnected(deploymentSse.connected);
    if (!deploymentSse.connected && alias) {
      void fetchDeploymentEvents();
      void fetchPipelineState().then((state: PipelineStep[]) =>
        setPipelineState(state),
      );
      void fetchMetrics();
    }
  }, [
    deploymentSse.connected,
    alias,
    fetchDeploymentEvents,
    fetchPipelineState,
    fetchMetrics,
    setPipelineState,
  ]);

  // ── SSE 2: pods + K8s events (sse/pods/:alias) ───────────────────────────

  const handlePodsMessage = useCallback(
    (msg: SseMessage) => {
      if (msg.type === "connected") {
        setPodsConnected(true);
        setPodError(null);
        setK8sEventsError(null);
        return;
      }

      if (msg.type === "pods") {
        setPodSnapshot(msg.data as PodMonitorSnapshot);
        setPodError(null);
        return;
      }

      if (msg.type === "events") {
        setK8sEvents(msg.data as K8sEventSnapshot[]);
        setK8sEventsError(null);
        return;
      }
    },
    [setPodSnapshot, setPodError, setK8sEvents, setK8sEventsError],
  );

  const podsSse = useSse(
    alias
      ? `sse/pods/${alias}?environment=${encodeURIComponent(appEnv)}`
      : null,
    handlePodsMessage,
  );

  // Sync pods SSE connection state. On disconnect, a one-shot REST refresh
  // primes the data — EventSource reconnects natively.
  useEffect(() => {
    setPodsConnected(podsSse.connected);
    if (!podsSse.connected && alias) {
      void fetchPods();
      void fetchK8sEvents();
    }
  }, [podsSse.connected, alias, fetchPods, fetchK8sEvents]);

  return {
    deploymentConnected,
    podsConnected,
  };
}
